import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import swal from "sweetalert";
import { useHistory } from "react-router-dom";
import { BASE_URL } from "../../Const/Url";
import { logout_func2 } from "../../Const/logoutCommon";
import { showToast } from "../../../utils/ToastHelper";
import Loader from "../../CustomCommons/Loader";
import DeleteIcon from "../../CustomCommons/DeleteIcon";
import EditIcon from "../../CustomCommons/EditIcon";
import plus from "../../../assets/Icon/plus.svg";

const LIMIT = 10;

const CustomNewArivalBannerList = () => {
  const history = useHistory();
  const [items, setItems] = useState([]);
  const [totalData, setTotalData] = useState(0);
  const [query, setQuery] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [pageNumber, setPageNumber] = useState(1);
  const [offset, setOffset] = useState(0);
  const [nextUrl, setNextUrl] = useState(null);
  const [prevUrl, setPrevUrl] = useState(null);

  const getOffsetFromUrl = (url) => {
    if (!url) return 0;
    try {
      const parsed = new URL(url);
      return Number(parsed.searchParams.get("offset") || 0);
    } catch (error) {
      const queryString = url?.split("?")[1] || "";
      const params = new URLSearchParams(queryString);
      return Number(params.get("offset") || 0);
    }
  };

  const fetchNewArrivalBanners = useCallback(
    (searchText = "", { urlOverride, offsetValue = 0 } = {}) => {
      setIsLoading(true);
      const resolvedOffset =
        typeof offsetValue === "number" && offsetValue >= 0 ? offsetValue : 0;

      let requestUrl = urlOverride;
      if (!requestUrl) {
        const params = new URLSearchParams();
        params.append("limit", LIMIT);
        params.append("offset", resolvedOffset);
        if (searchText) {
          params.append("search", searchText);
        }
        requestUrl = `${BASE_URL}api/v1/frontend/admin/v2/custom-new-arrival-banners/?${params.toString()}`;
      }

      axios
        .get(requestUrl)
        .then((res) => {
          const payload = res?.data?.data || {};
          setItems(payload?.results || []);
          setTotalData(payload?.count || 0);
          setNextUrl(payload?.next || null);
          setPrevUrl(payload?.previous || null);
          setOffset(resolvedOffset);
          setPageNumber(Math.floor(resolvedOffset / LIMIT) + 1);
        })
        .catch((err) => {
          const message = err?.response?.data?.message;
          const status = err?.response?.status;

          if (message === "Invalid token." || status === 401) {
            logout_func2(() => {}, history);
            return;
          }
          showToast("error", message || "Failed to load new arrival banners");
        })
        .finally(() => setIsLoading(false));
    },
    [history]
  );

  useEffect(() => {
    const debounce = setTimeout(() => {
      fetchNewArrivalBanners(query, { offsetValue: 0 });
    }, 400);

    return () => clearTimeout(debounce);
  }, [query, fetchNewArrivalBanners]);

  const totalPages = LIMIT ? Math.ceil((totalData || 0) / LIMIT) || 1 : 1;

  const handlePaginationPrevious = () => {
    if (!prevUrl) return;
    const derivedOffset = getOffsetFromUrl(prevUrl);
    fetchNewArrivalBanners(query, {
      urlOverride: prevUrl,
      offsetValue: derivedOffset,
    });
  };

  const handlePaginationNext = () => {
    if (!nextUrl) return;
    const derivedOffset = getOffsetFromUrl(nextUrl);
    fetchNewArrivalBanners(query, {
      urlOverride: nextUrl,
      offsetValue: derivedOffset,
    });
  };

  const handleCreate = () => {
    history.push("/website-settings/create-custom-new-arrival-banner");
  };

  const handleEdit = (item) => {
    const identifier = item?.slug || item?.id;
    if (!identifier) {
      showToast("error", "Unable to identify banner");
      return;
    }
    history.push(
      `/website-settings/edit-custom-new-arrival-banner/${identifier}`
    );
  };

  const handleDelete = (item) => {
    const identifier = item?.slug || item?.id;
    if (!identifier) {
      showToast("error", "Unable to identify banner");
      return;
    }

    swal({
      title: "Are you sure?",
      text: "Once deleted, you will not be able to recover this data.",
      icon: "warning",
      buttons: true,
      dangerMode: true,
    }).then((willDelete) => {
      if (!willDelete) {
        swal("Your data is safe!");
        return;
      }

      axios
        .delete(
          `${BASE_URL}api/v1/frontend/admin/v2/custom-new-arrival-banners/${identifier}/`
        )
        .then(() => {
          swal("New arrival banner deleted!", { icon: "success" });
          showToast("success", "New arrival banner deleted successfully");
          fetchNewArrivalBanners(query, { offsetValue: offset });
        })
        .catch((err) => {
          const message = err?.response?.data?.message;
          showToast("error", message || "Failed to delete new arrival banner");
        });
    });
  };

  const formatDate = (value) => {
    if (!value) return "-";
    return new Date(value).toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  if (isLoading) {
    return <Loader />;
  }

  return (
    <div className="categorey-parent">
      <div className="cotagorey-head mb-4">
        <div>
          <h3>Custom New Arrival Banners</h3>
          <input
            style={{ color: "black" }}
            type="text"
            className="filter-input"
            placeholder="Search here"
            onChange={(e) => {
              setQuery(e.target?.value);
            }}
          />
        </div>
        <div className="filter_div">
          <div className="button-div" onClick={handleCreate}>
            <img src={plus} alt="Add custom new arrival banner" />
            <span>Add New Arrival Banner</span>
          </div>
        </div>
      </div>

      <div style={{ display: "block", overflowX: "auto" }}>
        <table className="table_my pdf-table my-3">
          <thead
            className="bg-dark text-white head-row mt-3 main-head-print"
            style={{ height: "50px" }}
          >
            <tr>
              <th style={{ width: "100px", textAlign: "center" }}>#</th>
              <th style={{ width: "100px" }}>Media</th>
              <th style={{ minWidth: "220px" }}>Header</th>
              <th style={{ minWidth: "260px" }}>Sub Header</th>
              <th style={{ minWidth: "160px" }}>Button Text</th>
              <th style={{ minWidth: "140px" }}>Status</th>
              <th style={{ minWidth: "180px" }}>Created At</th>
              <th style={{ minWidth: "180px" }}>Updated At</th>
              <th style={{ width: "140px", textAlign: "center" }}>Action</th>
            </tr>
          </thead>
          <tbody className="tb">
            {items?.length ? (
              items.map((item, index) => (
                <tr key={item?.id || index} className="table-row">
                  <td className="text-center">{offset + index + 1}</td>
                  <td>
                    {item?.media_type === 0 ? (
                      <img
                        style={{
                          width: "50px",
                          height: "40px",
                          objectFit: "cover",
                        }}
                        src={item?.banner_details?.doc_url}
                        alt="New Arrival Banner"
                      />
                    ) : (
                      <div
                        style={{
                          width: "50px",
                          height: "40px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          backgroundColor: "#f0f0f0",
                          borderRadius: "4px",
                        }}
                      >
                        <svg
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path d="M8 5V19L19 12L8 5Z" fill="#666" />
                        </svg>
                      </div>
                    )}
                  </td>
                  <td>{item?.header || "-"}</td>
                  <td>{item?.sub_header || "-"}</td>
                  <td>{item?.button_text || "-"}</td>
                  <td>
                    {item?.is_active ? (
                      <span className="badge bg-success">Active</span>
                    ) : (
                      <span className="badge bg-secondary">Inactive</span>
                    )}
                  </td>
                  <td>{formatDate(item?.created_at)}</td>
                  <td>{formatDate(item?.updated_at)}</td>
                  <td className="action">
                    <div className="d-flex justify-content-center gap-3">
                      <div
                        className="edit-del green"
                        onClick={() => handleEdit(item)}
                      >
                        <EditIcon />
                      </div>
                      <div
                        className="edit-del red"
                        onClick={() => handleDelete(item)}
                      >
                        <DeleteIcon />
                      </div>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="9">
                  <div className="not_found d-flex justify-content-center align-items-center py-4">
                    <h4 className="my-4">No Data Found</h4>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {totalData > 0 && (
        <div className="row pag" style={{ marginTop: "30px" }}>
          <div className="col-12 col-md-4">
            <p className="inter" style={{ color: "#AEAEB2", fontSize: "16px" }}>
              Total Entries:{" "}
              <span style={{ color: "black", fontSize: "14px" }}>
                {totalData}
              </span>
            </p>
          </div>
          {totalPages > 1 && (
            <div className="col-12 col-md-8 d-flex justify-content-md-end">
              <div className="pagination-controls d-flex gap-2">
                <button
                  className="btn btn-sm btn-outline-dark"
                  onClick={handlePaginationPrevious}
                  disabled={!prevUrl}
                >
                  Prev
                </button>
                <span className="d-flex align-items-center">
                  Page {pageNumber} of {totalPages}
                </span>
                <button
                  className="btn btn-sm btn-outline-dark"
                  onClick={handlePaginationNext}
                  disabled={!nextUrl}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CustomNewArivalBannerList;
